import React, { createContext, useContext, useState, useEffect } from 'react';
import { SUPPORTED_LANGUAGES } from '../lib/i18n.config';
import i18n from '../i18n';

/**
 * Language Context
 * 
 * Provides global language management with persistence
 * Usage: const { language, setLanguage } = useLanguage()
 */
export const LanguageContext = createContext();

export const LanguageProvider = ({ children, defaultLanguage = 'en' }) => {
  const [language, setLanguageState] = useState(() => {
    // Get language from localStorage or use default
    const saved = localStorage.getItem('i18nextLanguage');
    return saved || defaultLanguage;
  });

  // Listen for i18n language changes and update local state
  useEffect(() => {
    const handleLanguageChanged = (newLang) => {
      setLanguageState(newLang);
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Persist language to localStorage and sync with i18n
  const setLanguage = (newLanguage) => {
    if (SUPPORTED_LANGUAGES[newLanguage]) {
      // Update document language attribute
      document.documentElement.lang = newLanguage;
      // Update local state immediately
      setLanguageState(newLanguage);
      localStorage.setItem('i18nextLanguage', newLanguage);
      // Sync with i18n instance
      if (i18n.isInitialized) {
        i18n.changeLanguage(newLanguage).catch(() => {
          // Silently handle errors, state already updated
        });
      }
    }
  };

  // Update document language on mount
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
    languages: SUPPORTED_LANGUAGES,
    isRTL: false, // African Polyglot languages are LTR
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 * @returns {object} { language, setLanguage, languages, isRTL }
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
